import { client } from '@/services/api/axios.ts';
import { useQuery } from '@tanstack/react-query';

type RangeGranularity = '1min' | '5min' | 'hourly' | 'daily' | 'monthly';

export type GetPlantEnergyManagementChartResponse = {
  Battery_Charge: number;
  Battery_Discharge: number;
  Consumed: number;
  Consumed_from_Grid: number;
  Fed_to_Grid: number;
  Yield: number;
  time: string;
}[];

export const useGetPlantEnergyManagementChartQuery = ({
  plantId,
  dateRange,
  enabled = true,
}: {
  plantId: string | undefined;
  dateRange: {
    startDate: Date;
    endDate: Date | undefined;
    rangeGranularity: RangeGranularity;
  };
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [
      'plants',
      plantId,
      'charts',
      dateRange.rangeGranularity,
      dateRange.startDate,
      dateRange.endDate,
    ],
    queryFn: async () => {
      const { data } = await client.get<{
        body: GetPlantEnergyManagementChartResponse;
      }>(`/plants/${plantId}/charts`, {
        params: {
          granularity: dateRange.rangeGranularity,
          startDate: `${new Date(dateRange.startDate).getTime()}`,
          endDate: `${dateRange.endDate && new Date(dateRange.endDate).getTime()}`,
        },
      });

      return data.body;
    },
    retry: 1,
    staleTime: getRefetchInterval(dateRange.rangeGranularity),
    refetchInterval: getRefetchInterval(dateRange.rangeGranularity),
    enabled,
  });
};

const getRefetchInterval = (rangeGranularity: RangeGranularity) => {
  if (!rangeGranularity) return undefined;

  if (rangeGranularity === '1min') return 1000 * 60;
  if (rangeGranularity === '5min') return 1000 * 60 * 5;

  if (rangeGranularity === 'hourly') return 1000 * 60 * 5;
  if (rangeGranularity === 'monthly') return 1000 * 60 * 5;
  if (rangeGranularity === 'daily') return 1000 * 60 * 5;
};
